import React from 'react';
import { Card } from '../components/ui/card';
import { FileText, AlertTriangle } from 'lucide-react';

/**
 * Conditions Générales d'Utilisation — TRADEX AI
 * Contenu conforme aux exigences légales françaises pour un SaaS B2C.
 * NOTE Jeremy: les champs entre [CROCHETS] doivent être renseignés avec les infos réelles de la société.
 */
export default function LegalCGU() {
  return (
    <div className="max-w-4xl mx-auto py-8 px-4 space-y-6" data-testid="legal-cgu-page">
      <div className="text-center space-y-3">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10">
          <FileText className="w-7 h-7 text-primary" />
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold">Conditions Générales d&apos;Utilisation</h1>
        <p className="text-xs text-muted-foreground">Dernière mise à jour : février 2026 · Version 1.0</p>
      </div>

      <Card className="p-4 border-amber-500/20 bg-amber-500/5 flex gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
        <div className="text-sm">
          <strong className="text-amber-300">Avertissement risque financier :</strong> TRADEX AI est un outil d&apos;aide à la décision. Les analyses fournies ne constituent pas un conseil en investissement. Le trading comporte des risques de perte totale du capital investi. Vous restez seul responsable de vos décisions.
        </div>
      </Card>

      <Card className="p-6 prose prose-invert max-w-none text-sm leading-relaxed space-y-4">
        <section>
          <h2 className="text-lg font-bold mt-0">1. Objet</h2>
          <p>Les présentes Conditions Générales d&apos;Utilisation (« CGU ») régissent l&apos;utilisation du service TRADEX AI accessible à l&apos;adresse <a href="https://tradex-ai.fr" className="text-primary hover:underline">tradex-ai.fr</a> (le « Service »), édité par [RAISON SOCIALE], [FORME JURIDIQUE] au capital de [MONTANT] €, immatriculée au RCS de [VILLE] sous le numéro [SIREN], dont le siège social est situé [ADRESSE COMPLÈTE] (« l&apos;Éditeur »).</p>
        </section>

        <section>
          <h2 className="text-lg font-bold">2. Description du Service</h2>
          <p>TRADEX AI est une application web (PWA) proposant :</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>L&apos;analyse par intelligence artificielle de graphiques de trading uploadés par l&apos;Utilisateur (via OpenAI GPT-4o Vision)</li>
            <li>Un système de suivi de trades avec calcul automatique de PnL, sorties partielles fixes, gestion du risque</li>
            <li>Un scanner automatisé (Scan Auto) sur une liste d&apos;actifs configurables</li>
            <li>Un journal de trades (paper et réel) avec historique et statistiques</li>
            <li>Des alertes temps réel via notifications push</li>
            <li>Un système de DCA (Dollar Cost Averaging) et de calculs de position</li>
          </ul>
          <p className="mt-2 text-muted-foreground text-xs">Le Service évolue régulièrement. La présente description est indicative et non contractuelle.</p>
        </section>

        <section>
          <h2 className="text-lg font-bold">3. Inscription et compte utilisateur</h2>
          <p>L&apos;inscription est ouverte aux personnes physiques majeures (18 ans ou plus). L&apos;Utilisateur s&apos;engage à fournir des informations exactes. Chaque compte est personnel et non transférable. Le mot de passe doit rester confidentiel — l&apos;Éditeur ne pourra être tenu responsable en cas d&apos;usage frauduleux du compte causé par la négligence de l&apos;Utilisateur.</p>
        </section>

        <section>
          <h2 className="text-lg font-bold">4. Formules d&apos;abonnement et tarifs</h2>
          <p>Le Service est proposé en plusieurs formules :</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Free</strong> : accès limité (nombre restreint d&apos;analyses IA par mois)</li>
            <li><strong>Pro mensuel</strong> : 19 €/mois — analyses IA étendues + Scan Auto</li>
            <li><strong>Expert mensuel</strong> : 39 €/mois — analyses illimitées + fonctionnalités avancées</li>
            <li><strong>Packs 3 mois / 1 an</strong> : paiement one-shot avec -10 % / -20 % de remise</li>
            <li><strong>Packs analyses</strong> : crédits ponctuels valables à vie sur le compte</li>
          </ul>
          <p className="mt-2">Les tarifs sont TTC. Le paiement est traité par Stripe. Toute souscription est activée immédiatement après confirmation du paiement.</p>
        </section>

        <section>
          <h2 className="text-lg font-bold">5. Droit de rétractation</h2>
          <p>Conformément à l&apos;article L221-28 du Code de la consommation, l&apos;Utilisateur reconnaît que le Service est un contenu numérique fourni immédiatement dès la souscription. En cliquant sur « Souscrire », l&apos;Utilisateur <strong>renonce expressément à son droit de rétractation</strong> de 14 jours pour la partie du service déjà consommée.</p>
          <p>À titre commercial, un remboursement peut être demandé dans les 7 jours suivant la première souscription, sous réserve qu&apos;aucune analyse IA n&apos;ait été consommée. Contactez-nous à <a href="/contact" className="text-primary hover:underline">/contact</a>.</p>
        </section>

        <section>
          <h2 className="text-lg font-bold">6. Résiliation</h2>
          <p>L&apos;Utilisateur peut résilier son abonnement à tout moment depuis <a href="/account/settings" className="text-primary hover:underline">Mes paramètres → Abonnement</a>. La résiliation prend effet à la fin de la période payée en cours. Aucun remboursement au prorata n&apos;est effectué pour la période restante, sauf disposition contraire prévue à l&apos;article 5.</p>
        </section>

        <section>
          <h2 className="text-lg font-bold">7. Obligations et responsabilité de l&apos;Utilisateur</h2>
          <p>L&apos;Utilisateur s&apos;engage à :</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Utiliser le Service dans le respect des lois en vigueur</li>
            <li>Ne pas contourner les limites d&apos;usage (ex. multi-comptes pour outrepasser un quota)</li>
            <li>Ne pas partager son compte avec des tiers</li>
            <li>Ne pas revendre ou redistribuer les analyses générées</li>
          </ul>
          <p className="mt-2 font-semibold">L&apos;Utilisateur reconnaît que les analyses IA sont fournies à titre indicatif uniquement. Il assume seul la responsabilité de ses décisions de trading et des pertes financières éventuelles.</p>
        </section>

        <section>
          <h2 className="text-lg font-bold">8. Propriété intellectuelle</h2>
          <p>L&apos;ensemble des éléments du Service (design, code, textes, logo, marque TRADEX AI) est protégé par le droit de la propriété intellectuelle. Toute reproduction totale ou partielle est interdite sans autorisation écrite de l&apos;Éditeur.</p>
          <p>Les graphiques uploadés par l&apos;Utilisateur restent sa propriété. L&apos;Utilisateur concède à l&apos;Éditeur une licence non-exclusive et temporaire pour les traiter via l&apos;IA.</p>
        </section>

        <section>
          <h2 className="text-lg font-bold">9. Disponibilité du Service</h2>
          <p>L&apos;Éditeur s&apos;engage à faire ses meilleurs efforts pour maintenir le Service disponible 24/7. Aucune garantie de disponibilité 100 % n&apos;est offerte. L&apos;Éditeur ne pourra être tenu responsable des indisponibilités liées à un cas de force majeure, à des tiers (Stripe, OpenAI, hébergeur cloud) ou à des interventions de maintenance planifiées.</p>
        </section>

        <section>
          <h2 className="text-lg font-bold">10. Limitation de responsabilité</h2>
          <p>L&apos;Éditeur ne pourra être tenu responsable :</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Des pertes financières résultant de décisions de trading prises par l&apos;Utilisateur</li>
            <li>Des erreurs ou approximations des analyses IA (l&apos;IA n&apos;est pas infaillible)</li>
            <li>D&apos;une indisponibilité temporaire du Service</li>
            <li>De pertes de données non imputables à l&apos;Éditeur</li>
          </ul>
          <p className="mt-2">Sauf dispositions légales impératives contraires, la responsabilité totale cumulée de l&apos;Éditeur envers l&apos;Utilisateur est limitée au montant payé par ce dernier sur les 12 derniers mois.</p>
        </section>

        <section>
          <h2 className="text-lg font-bold">11. Données personnelles</h2>
          <p>Le traitement des données personnelles est décrit dans notre <a href="/legal/rgpd" className="text-primary hover:underline">Politique de confidentialité (RGPD)</a>.</p>
        </section>

        <section>
          <h2 className="text-lg font-bold">12. Modification des CGU</h2>
          <p>L&apos;Éditeur se réserve le droit de modifier les présentes CGU à tout moment. L&apos;Utilisateur sera informé par email au moins 15 jours avant l&apos;entrée en vigueur d&apos;une modification substantielle. La poursuite de l&apos;utilisation du Service vaut acceptation des nouvelles CGU.</p>
        </section>

        <section>
          <h2 className="text-lg font-bold">13. Droit applicable et juridiction compétente</h2>
          <p>Les présentes CGU sont soumises au droit français. Tout litige relatif à l&apos;interprétation ou à l&apos;exécution des CGU fera l&apos;objet d&apos;une tentative de résolution amiable. À défaut, les tribunaux français compétents seront seuls compétents. Les consommateurs peuvent saisir gratuitement le médiateur de la consommation via la plateforme européenne : <a href="https://ec.europa.eu/consumers/odr" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">ec.europa.eu/consumers/odr</a>.</p>
        </section>

        <section className="mt-6 pt-4 border-t border-white/10 text-xs text-muted-foreground">
          <p><strong>Contact légal :</strong> <a href="mailto:contact@tradex-ai.fr" className="text-primary hover:underline">contact@tradex-ai.fr</a></p>
          <p><strong>Hébergeur :</strong> Emergent Cloud Platform</p>
        </section>
      </Card>
    </div>
  );
}
